import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../config/supabase';
import { User, Camera, Loader, MapPin } from '../../lib/lucide-proxy';
import toast from 'react-hot-toast';

const Profile = () => {
  const { profile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    bio: '',
    location: ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        location: profile.location || ''
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Ensure we only send fields that exist in the database schema
    const updates = {
      full_name: formData.full_name,
      phone: formData.phone,
      bio: formData.bio,
      location: formData.location,
      updated_at: new Date().toISOString()
    };

    const { error } = await updateProfile(updates);
    if (error) {
      console.error("Profile update error:", error);
      // Toast is handled in AuthContext, but we can add specific handling here if needed
    }
    setLoading(false);
  };

  const handleAvatarUpload = async (event) => {
    try {
      setUploading(true);
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Selecione uma imagem para upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`; // Added timestamp to avoid caching issues
      const filePath = `${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update Profile
      await updateProfile({ avatar_url: publicUrl });
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Erro ao fazer upload da foto: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Meu Perfil - Cerveja Delivery</title>
      </Helmet>
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-dark-900 mb-8">Meu Perfil</h2>
        
        <div className="flex flex-col items-center mb-8">
          <div className="relative group cursor-pointer">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-100 shadow-sm bg-gray-50 relative">
              {uploading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
                  <Loader className="w-8 h-8 text-white animate-spin" />
                </div>
              ) : null}
              
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <User className="w-16 h-16" />
                </div>
              )}
            </div>
            
            {/* Overlay Button */}
            <label 
              htmlFor="avatar-upload" 
              className="absolute bottom-0 right-0 bg-primary-500 text-white p-2 rounded-full shadow-lg cursor-pointer hover:bg-primary-600 transition-colors hover:scale-105 transform z-30"
              title="Alterar foto"
            >
              <Camera className="w-5 h-5" />
            </label>
            <input 
              type="file" 
              id="avatar-upload" 
              accept="image/*" 
              onChange={handleAvatarUpload} 
              className="hidden" 
              disabled={uploading}
            />
          </div>
          <p className="text-sm text-dark-500 mt-3">Clique na câmera para alterar sua foto</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input 
            label="Email" 
            value={profile?.email || ''} 
            disabled 
            className="opacity-75 bg-gray-50" 
          />
          
          <Input 
            label="Nome Completo" 
            name="full_name" 
            value={formData.full_name} 
            onChange={handleChange} 
            placeholder="Seu nome completo" 
          />
          
          <Input 
            label="Telefone / WhatsApp" 
            name="phone" 
            value={formData.phone} 
            onChange={handleChange} 
            placeholder="(00) 00000-0000" 
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input 
              label="Localização" 
              name="location" 
              value={formData.location} 
              onChange={handleChange} 
              placeholder="Cidade - UF" 
              icon={MapPin}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-2">Bio (Sobre você)</label>
            <textarea 
              name="bio" 
              rows={3} 
              className="input-field resize-none" 
              value={formData.bio} 
              onChange={handleChange} 
              placeholder="Conte um pouco sobre seus gostos cervejeiros..." 
            />
          </div>

          <div className="pt-4 flex justify-end border-t border-gray-100">
            <Button type="submit" variant="primary" loading={loading}>
              Salvar Alterações
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Profile;